import { useState, useEffect } from "react";
import {
  convertToCurrencyFormat,
  isReadOnlyUser,
} from "../../../shared/utility";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import ButtonSpinner from "../../controls/ButtonSpinner";
import { InputNumber } from "primereact/inputnumber";
import ReceivingSubTable1 from "./subtables/ReceivingSubtable1";
import { is } from "date-fns/locale";

export default function ReceivingItemsTableComponent(props) {
  const {
    loading,
    values,
    askReturnSelectedItems,
    selectedItems,
    setSelectedItems,
    onUpdateUnitCostForReceivingItem,
    onUpdateTotalCostForReceivingItem,
    closed,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    fetchRowDetails,
    reprintReceivingItem,
  } = props;

  // optional pagination props from parent
  const { onFetchPage, rowsPerPage: rowsPerPageProp } = props;
  const [currentPage, setCurrentPage] = useState(1);

  const [expandedRows, setExpandedRows] = useState(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(rowsPerPageProp || 15);

  const allowExpansion = (rowData) => {
    return true;
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.unique_id]) {
      fetchRowDetails(rowData);
    }
  };

  const rowExpansionTemplate = (data) => {
    return (
      <ReceivingSubTable1
        data={data}
        loadingRows={loadingRows}
        rowDetails={rowDetails}
        fetchSubRowDetails={fetchSubRowDetails}
        subRowDetails={subRowDetails}
        subLoadingRows={subLoadingRows}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        closed={closed}
      />
    );
  };

  // Determine whether items is paginated (Laravel LengthAwarePaginator shape)
  const isPaginated =
    values &&
    values.items &&
    typeof values.items === "object" &&
    !Array.isArray(values.items) &&
    Object.prototype.hasOwnProperty.call(values.items, "data") &&
    Object.prototype.hasOwnProperty.call(values.items, "current_page");

  const items = isPaginated
    ? values.items.data
    : Array.isArray(values.items)
    ? values.items
    : [];
  const perPage = isPaginated ? values.items.per_page : rowsPerPageProp || rows;
  const totalRecords = isPaginated ? values.items.total : items.length;

  // keep rows/perPage in sync when values change
  useEffect(() => {
    if (perPage && perPage !== rows) {
      setRows(perPage);
    }
    // rows intentionally excluded to avoid loop; setRows is safe here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  // sync `first` offset from incoming paginator (so UI stays in sync when parent updates)
  const itemsPagin = values && values.items ? values.items : null;
  useEffect(() => {
    if (isPaginated && itemsPagin) {
      const newFirst =
        (itemsPagin.current_page - 1) * (itemsPagin.per_page || rows);
      setFirst(newFirst);
    } else {
      setFirst(0);
    }
    // rows intentionally excluded to avoid loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginated, itemsPagin]);

  const bodyProductName = (item) => {
    return (
      <p>
        {item.product_name} <br /> {item.plu}
      </p>
    );
  };

  const bodyParentTotalWeight = (item) => {
    return (
      <p>
        {item.total_weight}
        {item.weight_stock_unit_of_measurement_name}
      </p>
    );
  };

  const bodyParentItemWeight = (item) => {
    return (
      <p>
        {item.net_weight}
        {item.weight_stock_unit_of_measurement_name}
      </p>
    );
  };

  const bodyParentPieceCount = (item) => {
    return <p>{item.total_pieces} pieces</p>;
  };

  const bodyParentUnitCost = (item) => {
    return <p>{convertToCurrencyFormat(item.unit_cost)}</p>;
  };

  const bodyParentTotalCost = (item) => {
    return <p>{convertToCurrencyFormat(item.total_cost)}</p>;
  };

  const bodyParentLocation = (item) => {
    return (
      <p>
        {item.warehouse_name} <br />
        {item.warehouse_location_name}
      </p>
    );
  };

  const onPriceEditComplete = (e) => {
    let { rowData, newValue, field, originalEvent: event } = e;
    if (newValue >= 0) {
      rowData[field] = newValue;
      onUpdateUnitCostForReceivingItem({
        unique_id: rowData.unique_id,
        plu: rowData.plu,
        lot_number: rowData.lot_number,
        unit_cost: newValue,
        warehouse_location_id: rowData.warehouse_location_id,
        is_paginated: isPaginated,
        page: currentPage,
      });
    } else {
      event.preventDefault();
    }
  };

  const onTotalCostEditComplete = (e) => {
    let { rowData, newValue, field, originalEvent: event } = e;
    if (newValue >= 0) {
      rowData[field] = newValue;
      onUpdateTotalCostForReceivingItem({
        unique_id: rowData.unique_id,
        plu: rowData.plu,
        lot_number: rowData.lot_number,
        total_cost: newValue,
        warehouse_location_id: rowData.warehouse_location_id,
        total_weight: rowData.total_weight,
        total_pieces: rowData.total_pieces,
        stock_unit_of_measurement: rowData.stock_unit_of_measurement,
      });
    } else {
      event.preventDefault();
    }
  };

  const unitPriceEditor = (options) => {
    return (
      <InputNumber
        value={options.value}
        onValueChange={(e) => options.editorCallback(e.value)}
        mode="currency"
        currency="USD"
        locale="en-US"
        onKeyDown={(e) => e.stopPropagation()}
      />
    );
  };

  const cartonCountBody = (item) => {
    return <p>{item.carton_count} cartons</p>;
  };

  const isReadOnly = isReadOnlyUser();

  return (
    <div className="flow-root">
      <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 space-y-4">
          <div className="w-full text-right">
            <button
              className="ml-2 rounded-md bg-main-purple px-1.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={() => reprintReceivingItem(selectedItems)}
            >
              <ButtonSpinner spin={false} text="Reprint" />
            </button>
            {!isReadOnly && (
              <button
                onClick={askReturnSelectedItems}
                className="ml-2 rounded-md bg-red-600 px-1.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              >
                Delete
              </button>
            )}
          </div>
          {loading ? (
            <div>
              <div className="border h-10 shadow rounded-md p-4 w-full mx-auto">
                <div className="animate-pulse flex space-x-4">Loading</div>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden card-custom bg-white">
              <DataTable
                value={items}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                }}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
                tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                cellClassName="p-2 border-1-bottom-gray text-sm"
                editMode="cell"
              >
                <Column
                  header="Product Name"
                  body={bodyProductName}
                  style={{ width: "25%" }}
                />
                <Column
                  field="lot_number"
                  header="Lot Number"
                  style={{ width: "15%" }}
                />
                <Column
                  header="Carton Count"
                  body={cartonCountBody}
                  style={{ width: "10%" }}
                />
                <Column
                  header="Unit Wt"
                  body={bodyParentItemWeight}
                  style={{ width: "5%" }}
                />
                <Column
                  header="Total Wt"
                  body={bodyParentTotalWeight}
                  style={{ width: "5%" }}
                />
                <Column
                  header="Total Pieces"
                  body={bodyParentPieceCount}
                  style={{ width: "8%" }}
                />
                <Column
                  header="Unit Cost"
                  body={bodyParentUnitCost}
                  style={{ width: "8%" }}
                  editor={(options) => unitPriceEditor(options)}
                  onCellEditComplete={onPriceEditComplete}
                />
                <Column
                  header="Total Cost"
                  body={bodyParentTotalCost}
                  style={{ width: "8%" }}
                  editor={(options) => unitPriceEditor(options)}
                  onCellEditComplete={onTotalCostEditComplete}
                />
                <Column
                  body={bodyParentLocation}
                  header="Location"
                  style={{ width: "15%" }}
                />
                <Column expander={allowExpansion} style={{ width: "6%" }} />
              </DataTable>
              {isPaginated ? (
                <div className="w-full">
                  <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPageChange={(event) => {
                      const nextPage = event.page + 1;
                      setFirst(event.first);
                      setRows(event.rows);
                      setCurrentPage(nextPage);
                      if (onFetchPage) onFetchPage(nextPage);
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
